import React, { useState, useEffect } from "react";
import { Search, Calendar, Globe, X, Edit3, Check, PlayCircle, AlertTriangle, Clock } from "lucide-react";
import { Spinner } from "../../auth/components/Shared/Icons";
import { useUiLang } from "../../../context/UiLangContext";
import type { TranscriptItem } from "../../translator/hooks/useTranslator";
import { useMeetingHistory, type Meeting } from "../hooks/useMeetingHistory";

interface HistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  translatorServiceUrl: string;
  onLoadMeeting: (
    meetingId: string,
    transcript: TranscriptItem[],
    srcLang: string,
    tgtLang: string
  ) => void;
  recording: boolean;
}

export function HistoryModal({
  isOpen,
  onClose,
  translatorServiceUrl,
  onLoadMeeting,
  recording,
}: HistoryModalProps) {
  const { uiLang } = useUiLang();
  const isEn = uiLang === "en";

  const {
    meetings,
    loading,
    error,
    renaming,
    loadingMeetingId,
    setError,
    clearMeetings,
    fetchMeetings,
    renameMeeting,
    loadMeetingTranscripts,
  } = useMeetingHistory(translatorServiceUrl);

  const [searchQuery, setSearchQuery] = useState("");
  const [editingId, setEditingId] = useState<string | number | null>(null);
  const [editTitleInput, setEditTitleInput] = useState("");

  useEffect(() => {
    if (isOpen) {
      fetchMeetings("", isEn);
    } else {
      clearMeetings();
      setSearchQuery("");
      setEditingId(null);
    }
  }, [isOpen, fetchMeetings, clearMeetings, isEn]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    fetchMeetings(searchQuery, isEn);
  };

  const startRename = (meeting: Meeting) => {
    setEditingId(meeting._id);
    setEditTitleInput(meeting.title);
  };

  const handleRenameSubmit = async (meetingId: string | number) => {
    const success = await renameMeeting(meetingId, editTitleInput, isEn);
    if (success) {
      setEditingId(null);
    }
  };

  const handleLoadMeeting = async (meeting: Meeting) => {
    const success = await loadMeetingTranscripts(meeting, recording, isEn, onLoadMeeting);
    if (success) {
      onClose();
    }
  };

  const formatDate = (dateStr: string) => {
    try {
      if (!dateStr) return "";
      let formattedStr = dateStr;
      if (formattedStr.includes(" ") && !formattedStr.includes("T")) {
        formattedStr = formattedStr.replace(" ", "T");
      }
      if (!formattedStr.endsWith("Z") && !/[+-]\d{2}:?\d{2}$/.test(formattedStr)) {
        formattedStr += "Z";
      }

      const d = new Date(formattedStr);
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleDateString(isEn ? "en-US" : "es-ES", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateStr;
    }
  };

  const formatDuration = (meeting: Meeting) => {
    const durVal = meeting.duration ?? meeting.duration_seconds ?? meeting.duration_ms ?? meeting.dur ?? meeting.segs ?? meeting.length;
    if (durVal !== undefined && durVal !== null && durVal !== "") {
      if (typeof durVal === "string" && (durVal.includes("min") || durVal.includes("seg") || durVal.includes("sec"))) {
        return durVal;
      }
      if (typeof durVal === "string" && durVal.includes(":")) {
        const parts = durVal.split(":");
        if (parts.length === 2) {
          const minsStr = (parts[0] || "00").padStart(2, "0");
          const secsStr = (parts[1] || "00").padStart(2, "0");
          return `${minsStr} min ${isEn ? "and" : "y"} ${secsStr} seg`;
        }
        if (parts.length === 3) {
          const hrsStr = (parts[0] || "00").padStart(2, "0");
          const minsStr = (parts[1] || "00").padStart(2, "0");
          const secsStr = (parts[2] || "00").padStart(2, "0");
          return parseInt(hrsStr) > 0
            ? `${hrsStr} h ${minsStr} min ${isEn ? "and" : "y"} ${secsStr} seg`
            : `${minsStr} min ${isEn ? "and" : "y"} ${secsStr} seg`;
        }
      }
      const numSec = Number(durVal);
      if (!isNaN(numSec) && numSec >= 0) {
        const totalSecs = numSec > 50000 ? Math.floor(numSec / 1000) : numSec;
        const minsStr = Math.floor(totalSecs / 60).toString().padStart(2, "0");
        const secsStr = Math.floor(totalSecs % 60).toString().padStart(2, "0");
        return `${minsStr} min ${isEn ? "and" : "y"} ${secsStr} seg`;
      }
    }
    return `00 min ${isEn ? "and" : "y"} 00 seg`;
  };

  const displayedMeetings = meetings.filter((meeting) => {
    if (!searchQuery.trim()) return true;
    return (meeting.title || "")
      .toLowerCase()
      .includes(searchQuery.trim().toLowerCase());
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-50 flex items-center justify-center p-4">
      <div
        className="bg-[var(--window-bg)] border border-[var(--border)] rounded-xl max-w-2xl w-full shadow-2xl overflow-hidden animate-[modal-appear_0.2s_cubic-bezier(0.16,1,0.3,1)] text-[var(--text)] flex flex-col max-h-[85vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 bg-[var(--surface)] border-b border-[var(--border)] flex-shrink-0">
          <div className="flex items-center gap-2">
            <Calendar size={18} className="text-[var(--accent)]" />
            <h2 className="text-sm font-semibold text-[var(--text)] uppercase tracking-wider font-mono">
              {isEn ? "Recording History" : "Historial de grabaciones"}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="w-6 h-6 rounded-md bg-[var(--surface)] hover:bg-[var(--surface-hover)] border border-[var(--border)] text-[var(--text-muted)] hover:text-[var(--text)] flex items-center justify-center transition-all"
            aria-label={isEn ? "Close" : "Cerrar"}
          >
            <X size={14} />
          </button>
        </div>

        {/* Search bar */}
        <div className="px-6 pt-5 pb-3 flex-shrink-0">
          <form onSubmit={handleSearchSubmit} className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-dim)]" size={14} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={isEn ? "Search by title..." : "Buscar por título..."}
                className="w-full bg-[var(--surface)] border border-[var(--border)] rounded-lg py-2 pl-9 pr-3 text-xs text-[var(--text)] placeholder-[var(--text-dim)]  focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent)]/20 transition-all"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-xs font-semibold text-white bg-accent hover:bg-accent/80 disabled:opacity-50 active:scale-95 rounded-lg shadow-md transition-all whitespace-nowrap"
            >
              {isEn ? "Search" : "Buscar"}
            </button>
          </form>

          {error && (
            <div className="flex items-start gap-2.5 px-4 py-3 bg-[var(--danger)]/10 border border-[var(--danger)]/20 text-[var(--danger)] rounded-xl text-xs font-semibold mt-3 leading-normal">
              <AlertTriangle size={15} className="mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>

        {/* List content */}
        <div className="flex-1 overflow-y-auto px-6 pb-6 space-y-3 min-h-0">
          {loading && meetings.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <Spinner sizeClass="w-8 h-8" borderClass="border-4 border-[var(--accent)]/20 border-t-[var(--accent)]" />
              <p className="text-xs text-[var(--text-muted)] font-mono animate-pulse">
                {isEn ? "Retrieving history..." : "Obteniendo grabaciones..."}
              </p>
            </div>
          ) : displayedMeetings.length === 0 ? (
            <div className="text-center py-16 text-xs text-[var(--text-dim)] border border-dashed border-[var(--border)] rounded-xl bg-black/5 dark:bg-white/[0.01]">
              {isEn ? "No past meetings found." : "No se encontraron reuniones guardadas."}
            </div>
          ) : (
            displayedMeetings.map((meeting) => (
              <div
                key={meeting._id}
                className="group border border-[var(--border)] bg-[var(--surface)] hover:bg-white/[0.03] dark:hover:bg-white/[0.01] p-4 rounded-xl flex items-center justify-between gap-4 transition-all duration-200"
              >
                <div className="flex-1 min-w-0 flex flex-col gap-1.5">
                  {/* Title editor */}
                  {editingId === meeting._id ? (
                    <input
                      type="text"
                      value={editTitleInput}
                      onChange={(e) => setEditTitleInput(e.target.value)}
                      onBlur={() => {
                        if (!editTitleInput.trim() || editTitleInput.trim() === meeting.title) {
                          setEditingId(null);
                        } else {
                          handleRenameSubmit(meeting._id);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          if (!editTitleInput.trim() || editTitleInput.trim() === meeting.title) {
                            setEditingId(null);
                          } else {
                            handleRenameSubmit(meeting._id);
                          }
                        } else if (e.key === "Escape") {
                          setEditingId(null);
                        }
                      }}
                      disabled={renaming}
                      className="bg-transparent border-b-2 border-accent px-1 text-xs text-text font-semibold font-mono focus:outline-none w-full"
                      autoFocus
                    />
                  ) : (
                    <h3
                      onClick={() => startRename(meeting)}
                      className="text-xs font-semibold text-text truncate font-mono cursor-text hover:bg-white/5 dark:hover:bg-white/5 px-1 rounded transition-colors"
                      title={isEn ? "Click to rename" : "Clic para renombrar"}
                    >
                      {meeting.title || (isEn ? "Untitled Meeting" : "Reunión sin título")}
                    </h3>
                  )}

                  {/* Metadata */}
                  <div className="flex flex-wrap items-center gap-y-1 gap-x-4 text-[10px] text-[var(--text-dim)] font-medium">
                    <span className="flex items-center gap-1.5 whitespace-nowrap">
                      <Calendar size={11} />
                      {formatDate(meeting.created_at)}
                    </span>
                    <span className="flex items-center gap-1.5 whitespace-nowrap">
                      <Clock size={11} />
                      {formatDuration(meeting)}
                    </span>
                    <span className="flex items-center gap-1 bg-black/10 dark:bg-white/[0.05] border border-[var(--border)] px-1.5 py-0.5 rounded font-mono text-[9px]">
                      <Globe size={10} />
                      {meeting.source_lang === "auto" ? "auto" : meeting.source_lang.toUpperCase()}
                      {" → "}
                      {meeting.target_lang.toUpperCase()}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <button
                  onClick={() => handleLoadMeeting(meeting)}
                  disabled={loadingMeetingId !== null && loadingMeetingId !== meeting._id}
                  className="inline-flex items-center gap-1.5 py-2 px-3 text-xs font-medium bg-[var(--accent-muted)] hover:bg-[var(--accent)] hover:text-white text-[var(--accent)] rounded-lg transition-all active:scale-95 disabled:opacity-50 whitespace-nowrap"
                >
                  {loadingMeetingId === meeting._id ? (
                    <>
                      <Spinner borderClass="border-2 border-[var(--accent)]/20 border-t-[var(--accent)]" />
                      <span>{isEn ? "Loading..." : "Cargando..."}</span>
                    </>
                  ) : (
                    <>
                      <PlayCircle size={14} />
                      <span>{isEn ? "Load Session" : "Cargar Sesión"}</span>
                    </>
                  )}
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
